
///
/// EarthBrowser
///
/// Created by Matt Giger on 12/8/2024
/// Copyright (c) 2024 Geodata Labs
///

// NOTE: When adding new fields, please make a migration and validation functions for them.

import { wgs } from "@/earthbrowser/utils/geographic"
import { type CameraSavedState } from "@/types/CameraSavedState"
import { type DatasetAction, type DatasetType } from "@/types/DatasetElement"
import { createSlice, type PayloadAction } from "@reduxjs/toolkit"
import type { BuoyRequestParams } from "@shared-types/buoy"
import type { EarthquakeRequestParams, WildfireRequestParams } from "@shared-types/data"
import type { LocationInfo } from "@shared-types/location-info"
import type { NexradAllStationsParams } from "@shared-types/nexrad"
import { trig } from "@stovokor/math/math"
import { cDefaultSatelliteSets } from "@/earthbrowser/dataset-distribution/satellites/satelliteSets"
import {
	cDefaultTourSettings,
	validateBuoySearchParams,
	validateCameraState,
	validateDatasetArray,
	validateEarthquakeSearchParams,
	validateLocationInfo,
	validateNexradSearchParams,
	validateSatelliteSets,
	validateTimeZoneMode,
	validateTourSettings,
	validateWildfireRequestParams
} from "./validation/earthBrowserValidation"

// Volcano view mode type
export type VolcanoViewMode = "topographical" | "3d"

// Time zone the clock is displayed and edited in
export type TimeZoneMode = "local" | "utc"

// GeoPackage layer visibility tracking
// Structure: { packageId: { layerName: boolean } }
export type GeoPackageLayerVisibility = Record<string, Record<string, boolean>>

// External GeoPackage reference (from File System Access API)
export interface ExternalGeoPackageRef {
	id: string
	name: string
	loaded: boolean
	visible: boolean
}

// Camera command routed to the 3D engine via ReduxActionBridge (no state change)
export interface CameraCommand {
	command: "zoom/in" | "zoom/out" | "zoom/set" | "flyTo/location" | "compass/reset" | "view/flyTo" | "orbit/toggle" | "camera/stopMotion" |
		"story/play" | "story/stop" | "story/pause" | "story/resume" | "story/next" | "story/prev" |
		// Device-location camera modes (CameraDirector / FollowBehavior):
		// follow centers on the fix as it moves, heading also turns the
		// view to the compass heading, stop returns the camera to free
		"location/follow" | "location/heading" | "location/stop" |
		// Storm column camera presets, handled by the focused StormColumnNode
		"column/view"
	data?: unknown
}

/** Payload for the "story/play" camera command */
export interface StoryPlayCommandData {
	storyId: string
	/** Stop to start at (default 0) */
	stopIndex?: number
	/** Autoplay with per-stop dwell timing (default false = reader-paced) */
	guided?: boolean
}

// Automated camera motion settings (persisted; consumed by CameraDirector)
export interface TourSettings {
	/** Start a slow orbit automatically after idleDelaySeconds of no input */
	autoOrbit: boolean
	/** Seconds of inactivity before auto-orbit kicks in (clamped 10-600) */
	idleDelaySeconds: number
	/** Orbit angular velocity in rad/s (clamped 0.002-0.2) */
	orbitSpeed: number
}

// Local data file from user's directory
export type LocalDataFileType = "geopackage" | "csv" | "shapefile" | "zip"

export interface LocalDataFile {
	name: string
	path: string
	type: LocalDataFileType
	size: number
	lastModified: number
}

export interface EarthBrowserState {
	cameraState: CameraSavedState
	cameraLocation: LocationInfo
	earthquakeSearchParams: EarthquakeRequestParams
	wildfireRequestParams: WildfireRequestParams
	/** Satellite sets on the globe (satelliteSets.ts ids); the layer loads their union */
	satelliteSets: string[]
	buoySearchParams: BuoyRequestParams
	nexradSearchParams: NexradAllStationsParams
	clockState: "animating" | "paused" | "real-time"
	clockAnimatingSpeed: number
	clockCurrentTime: number
	clockStartOffset: number
	clockEndOffset: number
	// Time zone used to display and edit clock times
	timeZoneMode: TimeZoneMode
	visibleDatasets: DatasetType[]
	loadingDatasets: DatasetType[]
	sideTableContent: DatasetType | null
	sideTableSize: number
	timeControlExpanded: boolean
	volcanoViewMode: VolcanoViewMode
	municipalityBordersVisible: boolean
	geopackageLayerVisibility: GeoPackageLayerVisibility
	geopackageDatasetVisible: Record<string, boolean>
	externalGeoPackages: Record<string, ExternalGeoPackageRef>
	geopackageDownloadDirectoryId: string | null
	localDataDirectoryId: string | null
	tourSettings: TourSettings
}

const initialState: EarthBrowserState = {
	cameraState: { radius: wgs.a + 30000, theta: 0, phi: trig.half_pi, tilt: 0, bearing: 0 },
	cameraLocation: { geoid: 0, name: "", admin1: "", admin2: "", country: "", tz: "", pop: 0, lat: 0, lon: 0, alt: 0, code: "" },
	earthquakeSearchParams: { count: 400, age: 30 * 86400 },
	wildfireRequestParams: { count: 100, age: 30 * 86400 },
	satelliteSets: [...cDefaultSatelliteSets],
	buoySearchParams: { count: 500, dart: false },
	nexradSearchParams: { hours: 2, count: 200 },
	clockState: "real-time",
	clockAnimatingSpeed: 600,
	clockCurrentTime: Date.now(),
	clockStartOffset: 48,
	clockEndOffset: 12,
	timeZoneMode: "local",
	visibleDatasets: ["cloud", "earthquake", "aurora", "hurricane", "location", "city_weather", "borders", "placemark", "fronts", "jetstream"],
	loadingDatasets: [],
	sideTableContent: null,
	sideTableSize: 20,
	timeControlExpanded: false,
	volcanoViewMode: "topographical",
	municipalityBordersVisible: false,
	geopackageLayerVisibility: {},
	geopackageDatasetVisible: {},
	externalGeoPackages: {},
	geopackageDownloadDirectoryId: null,
	localDataDirectoryId: null,
	tourSettings: cDefaultTourSettings,
}

export const earthBrowserSlice = createSlice({
	name: "earthbrowser",
	initialState,
	reducers: {
		setCameraState: (state, action: PayloadAction<CameraSavedState>) => {
			state.cameraState = validateCameraState(action.payload)
		},
		setCameraTilt: (state, action: PayloadAction<number>) => {
			const validated = validateCameraState({ ...state.cameraState, tilt: action.payload })
			state.cameraState.tilt = validated.tilt
		},
		setAnimatedCameraTilt: (state, action: PayloadAction<number>) => {
			const validated = validateCameraState({ ...state.cameraState, tilt: action.payload })
			state.cameraState.tilt = validated.tilt
		},
		setCameraBearing: (state, action: PayloadAction<number>) => {
			const validated = validateCameraState({ ...state.cameraState, bearing: action.payload })
			state.cameraState.bearing = validated.bearing
		},
		setCameraLocation: (state, action: PayloadAction<LocationInfo>) => {
			state.cameraLocation = validateLocationInfo(action.payload)
		},
		// No state change - the 3D engine subscribes to this action via ReduxActionBridge
		// and routes the command to the focused activity (zoom, fly-to, etc.)
		sendCameraCommand: (_state, _action: PayloadAction<CameraCommand>) => {},
		setTourSettings: (state, action: PayloadAction<Partial<TourSettings>>) => {
			state.tourSettings = validateTourSettings({ ...state.tourSettings, ...action.payload })
		},
		setEarthquakeSearchParams: (state, action: PayloadAction<EarthquakeRequestParams>) => {
			state.earthquakeSearchParams = validateEarthquakeSearchParams(action.payload)
		},
		setWildfireRequestParams: (state, action: PayloadAction<WildfireRequestParams>) => {
			state.wildfireRequestParams = validateWildfireRequestParams(action.payload)
		},
		setBuoySearchParams: (state, action: PayloadAction<BuoyRequestParams>) => {
			state.buoySearchParams = validateBuoySearchParams(action.payload)
		},
		setNexradSearchParams: (state, action: PayloadAction<NexradAllStationsParams>) => {
			state.nexradSearchParams = validateNexradSearchParams(action.payload)
		},
		setSatelliteSets: (state, action: PayloadAction<string[]>) => {
			state.satelliteSets = validateSatelliteSets(action.payload)
		},
		toggleSatelliteSet: (state, action: PayloadAction<string>) => {
			const id = action.payload
			const next = state.satelliteSets.includes(id)
				? state.satelliteSets.filter(s => s !== id)
				: [...state.satelliteSets, id]
			state.satelliteSets = validateSatelliteSets(next)
		},
		setClockState: (state, action: PayloadAction<"animating" | "paused" | "real-time">) => {
			state.clockState = action.payload
		},
		setClockAnimatingSpeed: (state, action: PayloadAction<number>) => {
			state.clockAnimatingSpeed = action.payload
		},
		setClockCurrentTime: (state, action: PayloadAction<number>) => {
			state.clockCurrentTime = action.payload
		},
		setClockStartOffset: (state, action: PayloadAction<number>) => {
			state.clockStartOffset = action.payload
		},
		setClockEndOffset: (state, action: PayloadAction<number>) => {
			state.clockEndOffset = action.payload
		},
		setTimeZoneMode: (state, action: PayloadAction<TimeZoneMode>) => {
			state.timeZoneMode = validateTimeZoneMode(action.payload)
		},
		sendDatasetAction: (state, action: PayloadAction<DatasetAction>) => {
			const value = action.payload
			switch (value.action) {
				case "enable":
					// Prevent duplicates
					if (!state.visibleDatasets.includes(value.type)) {
						state.visibleDatasets.push(value.type)
					}
					break
				case "disable":
					state.visibleDatasets = state.visibleDatasets.filter(dataset => dataset !== value.type)
					break
				case "refresh":
					break
			}
			// Validate array after modification
			state.visibleDatasets = validateDatasetArray(state.visibleDatasets)
		},
		addLoadingDataset: (state, action: PayloadAction<DatasetType>) => {
			// Prevent duplicates
			if (!state.loadingDatasets.includes(action.payload)) {
				state.loadingDatasets.push(action.payload)
			}
		},
		removeLoadingDataset: (state, action: PayloadAction<DatasetType>) => {
			state.loadingDatasets = state.loadingDatasets.filter(dataset => dataset !== action.payload)
		},
		setSideTableContent: (state, action: PayloadAction<DatasetType | null>) => {
			state.sideTableContent = action.payload
		},
		setSideTableSize: (state, action: PayloadAction<number>) => {
			state.sideTableSize = action.payload
		},
		setTimeControlExpanded: (state, action: PayloadAction<boolean>) => {
			state.timeControlExpanded = action.payload
		},
		toggleTimeControlExpanded: (state) => {
			state.timeControlExpanded = !state.timeControlExpanded
		},
		setVolcanoViewMode: (state, action: PayloadAction<VolcanoViewMode>) => {
			const mode = action.payload
			if (mode === "topographical" || mode === "3d") {
				state.volcanoViewMode = mode
			}
		},
		toggleVolcanoViewMode: (state) => {
			state.volcanoViewMode = state.volcanoViewMode === "topographical" ? "3d" : "topographical"
		},
		setMunicipalityBordersVisible: (state, action: PayloadAction<boolean>) => {
			state.municipalityBordersVisible = action.payload
		},
		toggleMunicipalityBordersVisible: (state) => {
			state.municipalityBordersVisible = !state.municipalityBordersVisible
		},
		// GeoPackage layer visibility management
		setGeoPackageDatasetVisible: (state, action: PayloadAction<{ packageId: string; visible: boolean }>) => {
			const { packageId, visible } = action.payload
			// Validate payload
			if (typeof packageId === "string" && packageId.length > 0 && typeof visible === "boolean") {
				state.geopackageDatasetVisible[packageId] = visible
			}
		},
		toggleGeoPackageDatasetVisible: (state, action: PayloadAction<string>) => {
			const packageId = action.payload
			// Validate payload
			if (typeof packageId === "string" && packageId.length > 0) {
				const current = state.geopackageDatasetVisible[packageId] ?? true
				state.geopackageDatasetVisible[packageId] = !current
			}
		},
		setGeoPackageLayerVisible: (state, action: PayloadAction<{ packageId: string; layerName: string; visible: boolean }>) => {
			const { packageId, layerName, visible } = action.payload
			// Validate payload
			if (typeof packageId === "string" && packageId.length > 0 &&
				typeof layerName === "string" && layerName.length > 0 &&
				typeof visible === "boolean") {
				if (!state.geopackageLayerVisibility[packageId]) {
					state.geopackageLayerVisibility[packageId] = {}
				}
				state.geopackageLayerVisibility[packageId][layerName] = visible
			}
		},
		toggleGeoPackageLayerVisible: (state, action: PayloadAction<{ packageId: string; layerName: string }>) => {
			const { packageId, layerName } = action.payload
			// Validate payload
			if (typeof packageId === "string" && packageId.length > 0 &&
				typeof layerName === "string" && layerName.length > 0) {
				if (!state.geopackageLayerVisibility[packageId]) {
					state.geopackageLayerVisibility[packageId] = {}
				}
				const current = state.geopackageLayerVisibility[packageId][layerName] ?? true
				state.geopackageLayerVisibility[packageId][layerName] = !current
			}
		},
		setAllGeoPackageLayersVisible: (state, action: PayloadAction<{ packageId: string; visible: boolean }>) => {
			const { packageId, visible } = action.payload
			// Validate payload
			if (typeof packageId === "string" && packageId.length > 0 && typeof visible === "boolean") {
				if (!state.geopackageLayerVisibility[packageId]) {
					state.geopackageLayerVisibility[packageId] = {}
				}
				// Set all layers in this package to the same visibility
				const layers = Object.keys(state.geopackageLayerVisibility[packageId])
				layers.forEach(layerName => {
					state.geopackageLayerVisibility[packageId][layerName] = visible
				})
			}
		},
		initializeGeoPackageLayers: (state, action: PayloadAction<{ packageId: string; layerNames: string[] }>) => {
			const { packageId, layerNames } = action.payload
			// Validate payload
			if (typeof packageId === "string" && packageId.length > 0 && Array.isArray(layerNames)) {
				if (!state.geopackageLayerVisibility[packageId]) {
					state.geopackageLayerVisibility[packageId] = {}
				}
				// Initialize any new layers that don't have a visibility setting
				layerNames.forEach(layerName => {
					// Validate each layer name
					if (typeof layerName === "string" && layerName.length > 0) {
						if (state.geopackageLayerVisibility[packageId][layerName] === undefined) {
							state.geopackageLayerVisibility[packageId][layerName] = true // Default to visible
						}
					}
				})
			}
		},
		// External GeoPackage management
		addExternalGeoPackage: (state, action: PayloadAction<{ id: string; name: string }>) => {
			const { id, name } = action.payload
			state.externalGeoPackages[id] = {
				id,
				name,
				loaded: false,
				visible: true
			}
		},
		setExternalGeoPackageLoaded: (state, action: PayloadAction<{ id: string; loaded: boolean }>) => {
			const { id, loaded } = action.payload
			if (state.externalGeoPackages[id]) {
				state.externalGeoPackages[id].loaded = loaded
			}
		},
		setExternalGeoPackageVisible: (state, action: PayloadAction<{ id: string; visible: boolean }>) => {
			const { id, visible } = action.payload
			if (state.externalGeoPackages[id]) {
				state.externalGeoPackages[id].visible = visible
			}
		},
		toggleExternalGeoPackageVisible: (state, action: PayloadAction<string>) => {
			const id = action.payload
			if (state.externalGeoPackages[id]) {
				state.externalGeoPackages[id].visible = !state.externalGeoPackages[id].visible
			}
		},
		removeExternalGeoPackage: (state, action: PayloadAction<string>) => {
			const id = action.payload
			delete state.externalGeoPackages[id]
		},
		// GeoPackage download directory management
		setGeoPackageDownloadDirectory: (state, action: PayloadAction<string | null>) => {
			// Validate that it's a string or null
			const directoryId = action.payload
			if (directoryId === null || (typeof directoryId === 'string' && directoryId.length > 0)) {
				state.geopackageDownloadDirectoryId = directoryId
			}
		},
		clearGeoPackageDownloadDirectory: (state) => {
			state.geopackageDownloadDirectoryId = null
		},
		// Local data directory management
		setLocalDataDirectory: (state, action: PayloadAction<string | null>) => {
			const directoryId = action.payload
			if (directoryId === null || (typeof directoryId === 'string' && directoryId.length > 0)) {
				state.localDataDirectoryId = directoryId
			}
		},
		clearLocalDataDirectory: (state) => {
			state.localDataDirectoryId = null
		},
		// Reset to initial state if corrupted state is detected
		resetToInitialState: () => {
			console.info("[State Reset] Resetting earthbrowser state to defaults")
			return initialState
		},
		// Apply validated state - used by validation middleware to fix invalid values
		applyValidatedState: (_state, action: PayloadAction<EarthBrowserState>) => {
			return action.payload
		},
	},
})

export const {
	setCameraState,
	setCameraTilt,
	setAnimatedCameraTilt,
	setCameraBearing,
	setCameraLocation,
	sendCameraCommand,
	setTourSettings,
	setEarthquakeSearchParams,
	setWildfireRequestParams,
	setSatelliteSets,
	toggleSatelliteSet,
	setBuoySearchParams,
	setNexradSearchParams,
	setClockState,
	setClockAnimatingSpeed,
	setClockCurrentTime,
	setClockStartOffset,
	setClockEndOffset,
	setTimeZoneMode,
	sendDatasetAction,
	addLoadingDataset,
	removeLoadingDataset,
	setSideTableContent,
	setSideTableSize,
	setTimeControlExpanded,
	toggleTimeControlExpanded,
	setVolcanoViewMode,
	toggleVolcanoViewMode,
	setMunicipalityBordersVisible,
	toggleMunicipalityBordersVisible,
	setGeoPackageDatasetVisible,
	toggleGeoPackageDatasetVisible,
	setGeoPackageLayerVisible,
	toggleGeoPackageLayerVisible,
	setAllGeoPackageLayersVisible,
	initializeGeoPackageLayers,
	addExternalGeoPackage,
	setExternalGeoPackageLoaded,
	setExternalGeoPackageVisible,
	toggleExternalGeoPackageVisible,
	removeExternalGeoPackage,
	setGeoPackageDownloadDirectory,
	clearGeoPackageDownloadDirectory,
	setLocalDataDirectory,
	clearLocalDataDirectory,
	resetToInitialState,
	applyValidatedState,
} = earthBrowserSlice.actions

export default earthBrowserSlice.reducer
